import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, LogOut, RefreshCw } from "lucide-react";
import type { User } from "@supabase/supabase-js";

import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import AdminSidebar, { type AdminSection } from "@/components/admin/AdminSidebar";
import AdminBookings from "@/components/admin/AdminBookings";
import AdminFleet from "@/components/admin/AdminFleet";
import AdminActivities from "@/components/admin/AdminActivities";
import AdminPhotos from "@/components/admin/AdminPhotos";
import AdminContent from "@/components/admin/AdminContent";
import AdminGallery from "@/components/admin/AdminGallery";
import AdminDestinations from "@/components/admin/AdminDestinations";
import AdminTestimonials from "@/components/admin/AdminTestimonials";
import AdminCustomTrips from "@/components/admin/AdminCustomTrips";
import AdminSecurity from "@/components/admin/AdminSecurity";
import GuideHub from "@/components/admin/blog/GuideHub";
import AdminAuthors from "@/components/admin/AdminAuthors";
import AdminBranding from "@/components/admin/AdminBranding";
import AdminBanners from "@/components/admin/AdminBanners";
import AdminFooter from "@/components/admin/AdminFooter";
import AdminAISettings from "@/components/admin/AdminAISettings";
import AdminTransferPricing from "@/components/admin/AdminTransferPricing";
import PageSEO from "@/components/PageSEO";

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>("guide");

  const isAdminUpRoute = location.pathname.toLowerCase() === "/adminup";
  const adminPath = location.pathname || "/admin";
  const adminSeo = (
    <PageSEO
      title="Admin — Maroc Transfers"
      description="Espace d'administration privé de Maroc Transfers : gestion des réservations, flotte, contenu et sécurité."
      path={adminPath}
      noindex
    />
  );

  const forceLogout = useCallback(async () => {
    await supabase.auth.signOut({ scope: "global" });
    setUser(null);
    setIsAdmin(false);
  }, []);

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!user || !isAdmin) return;

    const TIMEOUT_MS = 30 * 60 * 1000;
    let timer: number | undefined;

    const expire = async () => {
      sessionStorage.removeItem("admin_last_activity");
      toast.info("Session expirée après 30 minutes d'inactivité.");
      await forceLogout();
      navigate("/adminup", { replace: true });
    };

    const reset = () => {
      sessionStorage.setItem("admin_last_activity", Date.now().toString());
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(expire, TIMEOUT_MS);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const last = Number(sessionStorage.getItem("admin_last_activity") || 0);
        if (last && Date.now() - last >= TIMEOUT_MS) {
          expire();
        } else {
          reset();
        }
      }
    };

    const events: Array<keyof WindowEventMap> = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);
    reset();

    return () => {
      if (timer) window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user, isAdmin, forceLogout]);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const safetyTimer = window.setTimeout(() => {
      if (mounted) {
        console.warn("[Admin] auth bootstrap timed out, releasing spinner");
        setLoading(false);
      }
    }, 8000);

    const resolveRole = async (currentUser: User | null) => {
      if (!mounted) return;
      setUser(currentUser);
      try {
        if (currentUser) {
          const { data, error } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", currentUser.id)
            .eq("role", "admin")
            .maybeSingle();
          if (error) throw error;
          if (mounted) setIsAdmin(!!data);
        } else if (mounted) {
          setIsAdmin(false);
        }
      } catch (err: any) {
        console.error("[Admin] role lookup failed:", err);
        if (mounted) {
          setIsAdmin(false);
          toast.error("Impossible de vérifier les droits admin. Réessayez.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    try {
      const sub = supabase.auth.onAuthStateChange((_event, session) => {
        resolveRole(session?.user ?? null);
      });
      subscription = sub.data.subscription;
    } catch (err) {
      console.error("[Admin] onAuthStateChange failed:", err);
      if (mounted) setLoading(false);
    }

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => resolveRole(session?.user ?? null))
      .catch((err) => {
        console.error("[Admin] getSession failed:", err);
        if (mounted) {
          setIsAdmin(false);
          setLoading(false);
          toast.error("Erreur de session. Veuillez vous reconnecter.");
        }
      });

    return () => {
      mounted = false;
      window.clearTimeout(safetyTimer);
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isAdminUpRoute && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [loading, isAdmin, isAdminUpRoute, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) toast.error("Erreur de connexion: " + error.message);
    setLoginLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) { toast.error("Veuillez entrer votre adresse email d'abord."); return; }
    setResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
    if (error) toast.error("Erreur: " + error.message);
    else toast.success("Un email de réinitialisation a été envoyé.");
    setResetLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut({ scope: "global" });
    setUser(null);
    setIsAdmin(false);
    window.location.href = "/";
  };

  if (loading) {
    return (
      <>
        {adminSeo}
        <div className="min-h-screen bg-background flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!user || !isAdmin) {
    if (!isAdminUpRoute) return null;

    return (
      <>
      {adminSeo}
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-center text-gradient-gold">Admin — Maroc Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            {user && !isAdmin ? (
              <div className="text-center space-y-4">
                <p className="text-muted-foreground font-body">Ce compte n'a pas les droits administrateur.</p>
                <Button variant="outline" onClick={handleLogout}><LogOut className="mr-2 w-4 h-4" /> Se déconnecter</Button>
              </div>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block font-body text-sm text-muted-foreground mb-1">Email</label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@maroctransfers.com" />
                </div>
                <div>
                  <label className="block font-body text-sm text-muted-foreground mb-1">Mot de passe</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute inset-y-0 right-0 flex h-full w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-gradient-gold text-primary-foreground" disabled={loginLoading}>
                  {loginLoading ? "Connexion..." : "Se connecter"}
                </Button>
                <button type="button" onClick={handleForgotPassword} disabled={resetLoading} className="block w-full text-center text-sm text-muted-foreground hover:text-primary font-body mt-2 underline cursor-pointer">
                  {resetLoading ? "Envoi en cours..." : "Mot de passe oublié ?"}
                </button>
                <a href="/" className="block text-center text-sm text-muted-foreground hover:text-primary font-body mt-2">← Retour au site</a>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  return (
    <>
    {adminSeo}
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar
          active={activeSection}
          onSelect={setActiveSection}
          user={user}
          onLogout={handleLogout}
        />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center gap-2 border-b border-border px-4 sticky top-0 bg-background z-10">
            <SidebarTrigger />
            <h1 className="font-heading text-lg font-bold text-gradient-gold">Dashboard Admin</h1>
            <span className="ml-auto text-sm text-muted-foreground hidden sm:inline truncate">{user.email}</span>
          </header>
          <main className="flex-1 px-4 sm:px-6 py-6 overflow-x-hidden">
            {activeSection === "bookings" && <AdminBookings />}
            {activeSection === "custom-trips" && <AdminCustomTrips />}
            {activeSection === "fleet" && <AdminFleet />}
            {activeSection === "pricing" && <AdminTransferPricing />}
            {activeSection === "activities" && <AdminActivities />}
            {activeSection === "destinations" && <AdminDestinations />}
            {activeSection === "testimonials" && <AdminTestimonials />}
            {activeSection === "gallery" && <AdminGallery />}
            {activeSection === "guide" && <GuideHub />}
            {activeSection === "authors" && <AdminAuthors />}
            {activeSection === "content" && <AdminContent />}
            {activeSection === "photos" && <AdminPhotos />}
            {activeSection === "branding" && <AdminBranding />}
            {activeSection === "banners" && <AdminBanners />}
            {activeSection === "footer" && <AdminFooter />}
            {activeSection === "ai-settings" && <AdminAISettings />}
            {activeSection === "security" && <AdminSecurity />}
          </main>
        </div>
      </div>
    </SidebarProvider>
    </>
  );
};

export default Admin;
